// This file is not yet translated.
/* eslint-disable i18next/no-literal-string */

import { Search as SearchFilters } from "@outschool/business-rules";
import { SearchFilters as SearchFiltersType } from "@outschool/gql-backend-generated";
import { pathnameWithoutLocale, searchPath } from "@outschool/routes";
import { dayjs } from "@outschool/time";
import {
  useEsaWaitPeriod,
  useFundingProgramAbbr,
  useSelfElectedStateFundingProgram,
  useSession,
} from "@outschool/ui-auth";
import _ from "lodash";
import React, { useCallback, useContext, useEffect } from "react";
import { useHistory, useLocation, useParams } from "react-router-dom";

import { useActiveLearnerSearchFilters } from "../hooks/useActiveLearnerSearchFilters";
import { useSearchByLearner } from "../providers/SearchByLearnerProvider";

/**
 * Type extended for filters used in the UI but not sent to the backend
 * (userName is always paired with userUid, but allows us to reconstruct
 * the UI)
 */
export type ExtendedSearchFiltersType = SearchFiltersType & {
  userName?: string | null;
  originalSpelling?: string;
};

export type SetFiltersType = (
  category: string,
  partialFilters: ExtendedSearchFiltersType,
  options?: {
    overrideRouting?: boolean;
  },
) => void;

interface SearchFiltersContextValue {
  setFilters?: SetFiltersType;
  filters?: ExtendedSearchFiltersType;
}

export const SearchFiltersContext =
  React.createContext<SearchFiltersContextValue>({});

export function SearchFiltersContextProvider({
  children,
}: React.PropsWithChildren<{}>) {
  const { isLoggedIn } = useSession();
  const esaWaitPeriod = useEsaWaitPeriod();

  const history = useHistory();
  const location = useLocation();
  const params = useParams();

  const getActiveLearnerSearchFilters = useActiveLearnerSearchFilters();
  const { learner } = useSearchByLearner({
    onFilterChange: learner => {
      learnerRef.current = learner;
      if (pathnameWithoutLocale(location.pathname) === searchPath()) {
        setFilters("SearchByLearner", getActiveLearnerSearchFilters(learner));
      }
    },
  });
  const learnerRef = React.useRef(learner);

  const esaState = useFundingProgramAbbr();
  const { selfElectedStateFundingProgram, deactivateSearchFilters } =
    useSelfElectedStateFundingProgram();
  const [persistedFundingPrograms, setPersistedFundingPrograms] =
    React.useState(!!esaState ? [esaState] : undefined);

  useEffect(() => {
    if (
      esaState ||
      (!isLoggedIn &&
        selfElectedStateFundingProgram.searchFiltersActive &&
        selfElectedStateFundingProgram.stateAbbreviation)
    ) {
      setPersistedFundingPrograms([
        esaState || selfElectedStateFundingProgram.stateAbbreviation,
      ]);
    }

    // if the user logged in, we deactivate the search filters from the banner
    if (isLoggedIn && selfElectedStateFundingProgram.searchFiltersActive) {
      deactivateSearchFilters();
      const newFilters = !!esaState ? [esaState] : [];
      setPersistedFundingPrograms(newFilters);
    }
  }, [
    esaState,
    selfElectedStateFundingProgram?.searchFiltersActive,
    selfElectedStateFundingProgram?.stateAbbreviation,
    isLoggedIn,
    deactivateSearchFilters,
  ]);

  /* Filters priority (from lowest to highest priority):
      - filters from query string
  */
  const filters = React.useMemo(() => {
    const filtersFromQuery = SearchFilters.fromQueryAndParams(
      location.query,
      params,
    );
    const esaSearchDate = {
      startAfter: dayjs().add(esaWaitPeriod, "days").format("YYYY-MM-DD"),
    };
    const defaultScheduleFiltersToUse = esaState ? esaSearchDate : {};
    const _filters: Record<string, any> = {
      ...defaultScheduleFiltersToUse,
      fundingPrograms: persistedFundingPrograms,
      ...filtersFromQuery,
      ...(learnerRef.current?.uid && { learnerUid: learnerRef.current.uid }),
    };

    if (_filters?.age) {
      const age = _filters.age
        ?.split(",")
        ?.map((age: $TSFixMe) => parseInt(age))
        ?.filter((age: $TSFixMe) => age < 19)
        ?.join(",");

      if (age === "") {
        delete _filters["age"];
      } else {
        _filters.age = age;
      }
    }

    if (!!_filters?.adminTags?.trim()) {
      _filters.adminTags = _filters.adminTags.split(",");
    } else {
      _filters.adminTags = undefined;
    }

    if (_filters?.theme) {
      _filters.theme = SearchFilters.convertThemeQueryToFilter(_filters.theme);
    }

    if (!_filters?.fundingPrograms?.length) {
      _filters.fundingPrograms = undefined;
    }

    return _filters;
  }, [
    persistedFundingPrograms,
    location.query,
    params,
    esaState,
    esaWaitPeriod,
  ]);

  const setFilters = useCallback<SetFiltersType>(
    (filterCategory, partialFilters, options) => {
      if ("q" in partialFilters && "multiTermQuery" in filters) {
        delete filters.multiTermQuery;
      }

      if ("multiTermQuery" in partialFilters && "q" in filters) {
        delete filters.q;
      }

      const newFilters: SearchFiltersType = _.omitBy(
        SearchFilters.omitCatchalls({
          ...filters,
          ...partialFilters,
        }),
        _.isNil,
      );

      const oldFilters = _.omitBy(
        SearchFilters.omitCatchalls(filters),
        _.isNil,
      );

      if (!_.isEqual(oldFilters, newFilters)) {
        setPersistedFundingPrograms(newFilters.fundingPrograms ?? undefined);
      }

      let newFiltersWithFormattedTheme = Object.assign({}, newFilters);
      if (newFilters.theme) {
        newFiltersWithFormattedTheme.theme =
          SearchFilters.convertThemeFilterToQuery(newFilters.theme);
      }

      if (
        typeof newFiltersWithFormattedTheme?.includeInProgressFixedLengthLiveFormat ===
        "boolean"
      ) {
        // searchPath expects a string for boolean values
        // @ts-ignore
        newFiltersWithFormattedTheme.includeInProgressFixedLengthLiveFormat =
          newFiltersWithFormattedTheme.includeInProgressFixedLengthLiveFormat
            ? "true"
            : "false";
      }

      if (!options?.overrideRouting) {
        // Filter out learnerUid from URL
        const { learnerUid: _learnerUid, ...newFilters } =
          newFiltersWithFormattedTheme;
        history.push(searchPath(newFilters), {
          filterCategory,
        });
      }
    },
    [history, filters],
  );

  const value = React.useMemo(
    () => ({
      filters,
      setFilters,
    }),
    [filters, setFilters],
  );

  return (
    <SearchFiltersContext.Provider value={value}>
      {children}
    </SearchFiltersContext.Provider>
  );
}

export function useSearchFilters({
  throwOnNoContext = true,
}: {
  throwOnNoContext?: boolean;
} = {}): SearchFiltersContextValue {
  const context = useContext(SearchFiltersContext);
  if (!context.setFilters) {
    if (throwOnNoContext) {
      throw new Error("Called useSearchFilters without a SearchFiltersContext");
    } else {
      return {};
    }
  }
  return context;
}
